import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Card,
  Container,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Template {
  template_id: string;
  template_name: string;
  description: string;
  business_name: string;
  is_global: boolean;
  is_active: boolean;
}

const GlobalTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<{ business_id: string; business_name: string }[]>([]);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchTemplates();
    fetchBusinesses();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication error: No token found. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/check-templates/all-businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setTemplates(response.data);
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setTemplates(response.data.data);
      } else {
        setTemplates([]);
      }
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again with Owner/Super Admin credentials.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view templates from all businesses. Make sure you are logged in as an Owner or Super Admin.');
      } else if (err.response?.status === 404) {
        setError('Templates endpoint not found. Please check your backend configuration.');
      } else {
        setError(`Failed to fetch templates: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGlobal = async (templateId: string, currentGlobal: boolean) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      // Use the new dedicated global status endpoint
      const response = await axios.put(
        `${API_BASE_URL}/check-templates/${templateId}/global`,
        { is_global: !currentGlobal },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Toggle global response:', response.data);
      
      // Refresh templates
      fetchTemplates();
    } catch (err: any) {
      console.error('Error toggling global status:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to update template global status. Please try again.');
      }
    }
  };

  const fetchBusinesses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        setBusinesses(response.data);
      } else if (Array.isArray(response.data?.data)) {
        setBusinesses(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching businesses:', err);
    }
  };

  const openDuplicateDialog = (template: Template) => {
    setSelectedTemplateId(template.template_id);
    setNewTemplateName(template.template_name || '');
    setSelectedBusinessId('');
    setDuplicateOpen(true);
  };

  const handleDuplicateToBusiness = async () => {
    if (!selectedTemplateId || !selectedBusinessId) {
      setSnackbar({ open: true, message: 'Please select a business', severity: 'error' });
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setSnackbar({ open: true, message: 'No authentication token found', severity: 'error' });
        return;
      }
      const resp = await axios.post(
        `${API_BASE_URL}/check-templates/${selectedTemplateId}/duplicate-to-business`,
        { target_business_id: selectedBusinessId, template_name: newTemplateName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data?.success) {
        setSnackbar({ open: true, message: 'Template added to business', severity: 'success' });
        setDuplicateOpen(false);
        setSelectedTemplateId(null);
        setSelectedBusinessId('');
        fetchTemplates();
      } else {
        setSnackbar({ open: true, message: resp.data?.message || 'Failed to add template', severity: 'error' });
      }
    } catch (err: any) {
      console.error('Error duplicating template:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || err.message || 'Failed to add template', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Global Template Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 4, p: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto' }}>Check Templates</Typography>
        </Box>
        
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Global</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length > 0 ? (
                templates.map((template) => (
                  <TableRow key={template.template_id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {template.template_name}
                      </Typography>
                      {template.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {template.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {template.business_name || 'Unknown Business'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-block', 
                        px: 1.5, 
                        py: 0.5, 
                        borderRadius: 1,
                        bgcolor: template.is_active ? 'success.light' : 'error.light',
                        color: template.is_active ? 'success.dark' : 'error.dark',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={template.is_global}
                            onChange={() => handleToggleGlobal(template.template_id, template.is_global)}
                            color="primary"
                            sx={{ 
                              '& .MuiSvgIcon-root': { fontSize: 20 },
                              py: 0.5,
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: template.is_global ? 600 : 400 }}>
                            Global
                          </Typography>
                        }
                        sx={{ 
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            color: template.is_global ? 'primary.main' : 'text.secondary'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button variant="outlined" size="small" onClick={() => openDuplicateDialog(template)}>
                        Add to Business
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No templates found.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Templates will appear here once businesses create them.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={duplicateOpen} onClose={() => setDuplicateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Template to Business</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="business-select-label">Business</InputLabel>
              <Select
                labelId="business-select-label"
                label="Business"
                value={selectedBusinessId}
                onChange={(e) => setSelectedBusinessId(String(e.target.value))}
              >
                {businesses.map((b) => (
                  <MenuItem key={b.business_id} value={b.business_id}>{b.business_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Template Name (optional)"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateOpen(false)}>Cancel</Button>
          <Button onClick={handleDuplicateToBusiness} variant="contained" disabled={submitting || !selectedBusinessId}>
            {submitting ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GlobalTemplateManagement; 